/**
 * useTechWatchNotifications Hook
 * Polls for technology watch updates and shows toast notifications
 */

import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { apiService } from '@/services/api';
import { useAuthStore } from '@/store/authStore';

import { secureLogger } from '@/utils/secureLogger';
interface TechWatchAlert {
  browserName: string;
  type: 'new_version' | 'security_advisory' | 'critical_update';
  message: string;
  severity: 'info' | 'warning' | 'critical';
  version: string;
  timestamp: string;
}

interface AlertsResponse {
  hasUpdates: boolean;
  alerts: TechWatchAlert[];
  lastCheckTimestamp: string;
}

// Store displayed alert IDs to avoid duplicate toasts
const displayedAlerts = new Set<string>();

/**
 * Custom hook that polls for technology watch updates and shows toast notifications
 * @param pollingInterval - Interval in milliseconds (default: 2 minutes)
 * @param enabled - Whether the hook should be active (default: true)
 */
export function useTechWatchNotifications(
  pollingInterval: number = 2 * 60 * 1000,
  enabled: boolean = true
) {
  const { isAuthenticated } = useAuthStore();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstCheck = useRef(true);

  const checkForAlerts = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiService.get('/technology-watch-v2/alerts');
      
      if (!response.success) return;

      const data = (response as any).data as AlertsResponse;
      
      if (!data?.hasUpdates || !data.alerts?.length) return;

      // Process each alert
      for (const alert of data.alerts) {
        // Create unique ID for this alert
        const alertId = `${alert.browserName}-${alert.type}-${alert.version}`;
        
        // Skip if already displayed
        if (displayedAlerts.has(alertId)) continue;
        
        // Mark as displayed
        displayedAlerts.add(alertId);

        // Show toast based on severity
        const toastOptions = {
          duration: alert.severity === 'critical' ? 10000 : 6000,
          icon: getAlertIcon(alert.severity),
        };

        switch (alert.severity) {
          case 'critical':
            toast.error(
              createToastContent(alert),
              {
                ...toastOptions,
                style: {
                  background: '#FEE2E2',
                  color: '#991B1B',
                  border: '1px solid #F87171',
                },
              }
            );
            break;
          case 'warning':
            toast(
              createToastContent(alert),
              {
                ...toastOptions,
                style: {
                  background: '#FEF3C7',
                  color: '#92400E',
                  border: '1px solid #FCD34D',
                },
              }
            );
            break;
          default:
            toast(
              createToastContent(alert),
              {
                ...toastOptions,
                icon: 'INFO',
                style: {
                  background: '#DBEAFE',
                  color: '#1E40AF',
                  border: '1px solid #93C5FD',
                },
              }
            );
        }
      }

      // Auto-acknowledge alerts after displaying (only for non-critical)
      const nonCriticalBrowsers = data.alerts
        .filter(a => a.severity !== 'critical')
        .map(a => a.browserName);
      
      if (nonCriticalBrowsers.length > 0) {
        await apiService.post('/technology-watch-v2/alerts/acknowledge', {
          browserNames: nonCriticalBrowsers
        }).catch(() => {
          // Silently ignore acknowledgment errors
        });
      }
    } catch (error) {
      // Silently fail - don't show error toasts for background polling
      secureLogger.debug('Tech watch notification check failed:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    // Initial check after a short delay (give the page time to load)
    const initialTimeout = setTimeout(() => {
      if (isFirstCheck.current) {
        isFirstCheck.current = false;
        // Don't show toasts on first check to avoid overwhelming users on page load
        // Just populate the displayed alerts set
        checkForAlertsQuietly();
      }
    }, 5000);

    // Set up polling interval
    intervalRef.current = setInterval(checkForAlerts, pollingInterval);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, isAuthenticated, pollingInterval, checkForAlerts]);

  // Quiet check on first load - just populate displayed set without toasts
  const checkForAlertsQuietly = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiService.get('/technology-watch-v2/alerts');
      
      if (!response.success) return;

      const data = (response as any).data as AlertsResponse;
      
      if (!data?.hasUpdates || !data.alerts?.length) return;

      // Just mark existing alerts as "seen" without showing toasts
      for (const alert of data.alerts) {
        const alertId = `${alert.browserName}-${alert.type}-${alert.version}`;
        displayedAlerts.add(alertId);
      }
    } catch (error) {
      secureLogger.debug('Initial tech watch check failed:', error);
    }
  };

  // Manual trigger function for components that want to force a check
  const forceCheck = useCallback(() => {
    checkForAlerts();
  }, [checkForAlerts]);

  return { forceCheck };
}

/**
 * Get icon based on severity
 */
function getAlertIcon(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'ALERT';
    case 'warning':
      return 'WARN';
    default:
      return 'INFO';
  }
}

/**
 * Create toast content with browser name and message
 */
function createToastContent(alert: TechWatchAlert): string {
  const typeLabel = alert.type === 'critical_update' 
    ? 'CRITICAL' 
    : alert.type === 'security_advisory' 
      ? 'Security' 
      : 'Update';
  
  return `[${typeLabel}] ${alert.message}`;
}

export default useTechWatchNotifications;
